import React, { useState } from 'react';
import { Task, Lead, CallWindow } from '../types';
import { istDay, istToday, isLate } from '../lib';
import { WhatsAppIcon, CallIcon } from '../components/BrandIcons';
import CallModal from '../components/CallModal';
import WhatsAppChat from '../components/WhatsAppChat';

/** Shared task-queue vocabulary. Today, Queue and Visits all read the same
 *  `tasks` rows, so how a task describes itself is defined once, here. */

/** Callback tasks carry their purpose under different payload keys depending on
 *  who minted them (selldo followup / welcome call / AI suggestion). Plenty of
 *  rows carry none — null, so callers can omit it rather than print a dash. */
export const purposeOf = (t: Task): string | null =>
  t.payload?.title || t.payload?.subject || t.payload?.summary || t.payload?.reason || null;

/** Lead snapshots reach us with blank names often enough to matter. Fall back
 *  to something dialable rather than rendering an empty row. */
export const leadName = (t: { lead?: { name?: string | null; mobile?: string | null } | null }): string =>
  t.lead?.name?.trim() || t.lead?.mobile?.trim() || 'Unknown lead';

/** A visit's rail colour is its confirmation state, not its time.
 *  Unconfirmed and imminent is the thing that must catch the eye. */
export const visitTone = (t: Task): 'brick' | 'gold' | 'sky' => {
  if (t.payload?.reconfirmed_at) return 'gold';
  if (istDay(t.due_at) === istToday()) return 'brick';
  return 'sky';
};

/** A dial was just made against this task and its outcome is being checked —
 *  it resolves (done or rescheduled) within minutes, so due-time/overdue
 *  styling would contradict what's actually happening. */
export const isCheckingCall = (t: Task) =>
  t.status === 'open' && t.type === 'call' && !!t.pending_result_activity_id;

/** Past its time — measured against the task's calling window when it has one
 *  (see `isLate`). The one predicate behind every brick-red due state; a dial
 *  being checked is never overdue. */
export const isOverdue = (t: Task, windows: CallWindow[]) =>
  isLate(t.due_at, windows) && !isCheckingCall(t);

/** Dial, raised from any row. One CallModal per page, never one per row. */
export const useCallModal = () => {
  const [lead, setLead] = useState<Lead | null>(null);
  return {
    call: (l?: Lead | null) => l && setLead(l),
    modal: lead ? <CallModal lead={lead} onClose={() => setLead(null)} /> : null,
  };
};

/** The Bhuvana WhatsApp chat, opened in place from any row. Like the Nurture
 *  app, a lead with no conversation yet has no chat to open. */
type ChatTarget = { conversationId: string; title: string; subtitle?: string };
export const useChatSheet = () => {
  const [target, setTarget] = useState<ChatTarget | null>(null);
  return {
    chat: (t: ChatTarget) => setTarget(t),
    chatLead: (l?: Lead | null) => l?.conversation_id && setTarget({
      conversationId: l.conversation_id, title: l.name, subtitle: l.mobile || undefined,
    }),
    sheet: target ? (
      <WhatsAppChat
        conversationId={target.conversationId}
        title={target.title}
        subtitle={target.subtitle}
        onClose={() => setTarget(null)}
      />
    ) : null,
  };
};

/** Reach affordances on a task row: dial, and the official WhatsApp chat. */
export const TaskActions: React.FC<{
  task: Task;
  onCall?: () => void;
  onChat: () => void;
}> = ({ task, onCall, onChat }) => (
  <span className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
    <button className="icb" title="Call" disabled={!task.lead?.mobile} onClick={onCall}>
      <CallIcon className="w-[18px] h-[18px]" />
    </button>
    <button className="icb" title={task.lead?.conversation_id ? 'WhatsApp' : 'No WhatsApp conversation yet'}
      disabled={!task.lead?.conversation_id} onClick={onChat}>
      <WhatsAppIcon className="w-[18px] h-[18px]" />
    </button>
  </span>
);
